// frontend/src/pages/BulkAnalysisPage/BatchSelector.jsx

import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, CircularProgress, IconButton, Alert, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';

const BatchSelector = ({ onSelectBatch, onCreateNew, setNotification }) => {
    const { t } = useTranslation();
    const { fetchWithAuth } = useAuth();
    const { setIsPageBlurred } = useContext(UIContext);
    const [batches, setBatches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isSelecting, setIsSelecting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    // State for the new batch dialog
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newBatchName, setNewBatchName] = useState('');
    const [newBatchDescription, setNewBatchDescription] = useState('');
    const [createError, setCreateError] = useState('');
    // State for the delete confirmation dialog
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [batchToDelete, setBatchToDelete] = useState(null);
    const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
    const [deleteAllConfirmation, setDeleteAllConfirmation] = useState('');
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const confirmKeyword = t('bulkAnalysisPage.deleteAll.confirmKeyword');
    const [page, setPage] = useState(0); // To track the current page/offset
    const [hasMore, setHasMore] = useState(true); // To know if we should show the "Load More" button
    const BATCH_LIMIT = 5; // Define the limit as a constant

    const fetchBatches = useCallback(async (isInitialLoad = false) => {
        // Don't fetch if we know there are no more batches
        if (!hasMore && !isInitialLoad) return;

        setIsLoading(true);
        try {
            const offset = isInitialLoad ? 0 : page * BATCH_LIMIT;
            const response = await fetchWithAuth(`/api/bulk/batches?search=${search}&limit=${BATCH_LIMIT}&offset=${offset}`);
            const newBatches = await response.json();
            
            // If we get fewer batches than the limit, we've reached the end
            setHasMore(newBatches.length === BATCH_LIMIT);

            if (isInitialLoad) {
                setBatches(newBatches);
                setPage(1); // Set up for the next page fetch
            } else {
                setBatches(prev => [...prev, ...newBatches]);
                setPage(prev => prev + 1);
            }
        } catch (error) {
            console.error("Failed to fetch batches", error);
        } finally {
            setIsLoading(false);
        }
    }, [search, page, hasMore, fetchWithAuth]);

    // This effect handles the initial load and resets when search changes
    useEffect(() => {
        // Reset state for a new search
        setBatches([]);
        setPage(0);
        setHasMore(true);
        // Using a timer to debounce the search input
        const timerId = setTimeout(() => {
             // We pass true to signal it's a fresh load/search
            fetchBatches(true);
        }, 300);
        return () => clearTimeout(timerId);
    // VETRA_AI_IGNORE_NEXT
    }, [search, fetchWithAuth]); // `fetchBatches` is not a dependency here to avoid re-triggering
    
    const handleSelect = async (batchId) => {
        setIsSelecting(true); // Start loading
        try {
            const response = await fetchWithAuth(`/api/bulk/batches/${batchId}`);
            const fullBatchData = await response.json();
            onSelectBatch(fullBatchData);
        } catch (error) {
            console.error("Failed to select batch", error);
            setIsSelecting(false);
        }
    };

    // --- CREATE LOGIC ---
    const handleOpenCreateDialog = () => {
        setNewBatchName(t('bulkAnalysisPage.dialogs.create.defaultName', { date: new Date().toLocaleDateString() }));
        setNewBatchDescription('');
        setCreateError('');
        setIsCreateDialogOpen(true);
    };
    
    const handleCloseCreateDialog = () => {
        setIsCreateDialogOpen(false);
    };

    const handleConfirmCreate = async () => {
        const trimmedName = newBatchName.trim();
        if (!trimmedName) {
            setCreateError(t('bulkAnalysisPage.dialogs.create.errorEmpty'));
            return;
        }

        setIsCreating(true); // Start loading
        const result = await onCreateNew(trimmedName, newBatchDescription.trim()); 
        setIsCreating(false); // Stop loading

        if (result.success) {
            setIsCreateDialogOpen(false);
            setNewBatchName('');
            setNewBatchDescription('');
        } else {
            if (result.errorKey === 'DUPLICATE_BATCH_NAME') {
                const message = t('bulkAnalysisPage.dialogs.create.errorDuplicate', {
                    batchName: result.errorDetails?.batchName || trimmedName
                });
                setCreateError(message);
            } else {
                setCreateError(result.message || t('bulkAnalysisPage.notifications.createError'));
            }
        }
    };

    // --- DELETE LOGIC ---
    const handleDeleteClick = (batch, event) => {
        event.stopPropagation(); 
        setBatchToDelete(batch);
        setIsConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        setIsConfirmOpen(false);
        setBatchToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!batchToDelete) return;
        setIsDeleting(true); // <-- Start loading
        try {
            await fetchWithAuth(`/api/bulk/batches/${batchToDelete.batch_id}`, {
                method: 'DELETE'
            });
            setBatches(prev => prev.filter(b => b.batch_id !== batchToDelete.batch_id));
            setNotification({ open: true, message: t('bulkAnalysisPage.notifications.batchDeleteSuccess'), severity: 'success' });
        } catch (error) {
            console.error("Failed to delete batch", error);
            setNotification({ open: true, message: t('bulkAnalysisPage.notifications.batchDeleteFailed'), severity: 'error' });
        } finally {
            setIsDeleting(false); // <-- Stop loading
            handleCloseConfirm();
        }
    };

    const handleLoadMore = () => {
        // We pass false because this is not an initial load
        fetchBatches(false);
    };

    // --- NEW DELETE ALL HANDLERS ---
    const handleOpenDeleteAll = () => {
        setDeleteAllConfirmation('');
        setIsDeleteAllOpen(true);
    };

    const handleCloseDeleteAll = () => {
        setIsDeleteAllOpen(false);
        setDeleteAllConfirmation('');
    };

    const handleConfirmDeleteAll = async () => {
        setIsDeletingAll(true);
        try {
            const response = await fetchWithAuth('/api/bulk/batches/all', {
                method: 'DELETE'
            });
            
            if (!response.ok) throw new Error("Failed to delete all batches");

            // Clear local state
            setBatches([]);
            setHasMore(false);
            setNotification({ open: true, message: t('bulkAnalysisPage.notifications.batchDeleteSuccess'), severity: 'success' });
            handleCloseDeleteAll();
        } catch (error) {
            console.error("Failed to delete all batches", error);
            setNotification({ open: true, message: t('bulkAnalysisPage.notifications.batchDeleteFailed'), severity: 'error' });
        } finally {
            setIsDeletingAll(false);
        }
    };

    useEffect(() => {
        const shouldBeBlurred = isConfirmOpen || isCreateDialogOpen || isDeleteAllOpen;
        setIsPageBlurred(shouldBeBlurred);

        // This cleanup function is crucial. It ensures the blur is removed 
        // if the component unmounts while a dialog is open.
        return () => setIsPageBlurred(false);
    }, [isConfirmOpen, isCreateDialogOpen, isDeleteAllOpen, setIsPageBlurred]);

    return (
        <Box 
            component="section" 
            aria-labelledby="batch-selector-title" 
            className="welcome-box glass-card"
        >
            <Typography variant="h5" component="h2" id="batch-selector-title" gutterBottom>
                {t('bulkAnalysisPage.batchSelector.title')}
            </Typography>
            <Button
                variant="contained" color="secondary"
                startIcon={<AddCircleOutlineIcon />} onClick={handleOpenCreateDialog}
                className="create-batch-button"
            >
                {t('bulkAnalysisPage.batchSelector.createNew')}
            </Button>
            <TextField
                label={t('bulkAnalysisPage.batchSelector.searchPlaceholder')}
                variant="outlined"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                    mt: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--secondary-color)' },
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                        '&.Mui-focused fieldset': { borderColor: 'var(--secondary-color)' },
                    },
                }}
            />

            {isLoading && batches.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                    <CircularProgress sx={{ color: 'white' }} />
                    <Button
                        disabled
                        variant="text"
                        sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7) !important', textTransform: 'none' }}
                    >
                        {t('bulkAnalysisPage.batchSelector.loadingBatches')}
                    </Button>
                </Box>
            ) : isSelecting ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, minHeight: '150px' }}>
                    <CircularProgress sx={{ color: 'white' }} />
                    <Button
                        disabled
                        variant="text"
                        sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7) !important', textTransform: 'none' }}
                    >
                        {t('bulkAnalysisPage.batchSelector.preparingTable')}
                    </Button>
                </Box>
            ) : (
                <Box 
                    className="batch-list-container inner-dark-card"
                    sx={{ width: '100%', mt: 1 }}
                >
                    {batches.length > 0 ? (
                        batches.map(batch => (
                            <div key={batch.batch_id} className="batch-selector-item-card" onClick={() => handleSelect(batch.batch_id)}>
                                <Tooltip title={t('bulkAnalysisPage.batchSelector.deleteTooltip')}>
                                    <IconButton 
                                        className="batch-delete-icon"
                                        aria-label="delete batch" 
                                        onClick={(e) => handleDeleteClick(batch, e)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                                <img 
                                    src={batch.batch_mode === 'dcm' ? '/static/previews/platform/dcm.png' : '/static/previews/platform/vetra_ai.png'}
                                    alt={batch.batch_mode === 'dcm' ? 'DCM Mode' : 'Manual Mode'}
                                    className="batch-selector-icon"
                                />
                                <div className="batch-item-content">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" className="batch-item-name">{batch.batch_name}</Typography>
                                        {batch.description && (
                                            <Tooltip title={batch.description} arrow placement="top">
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.6)', cursor: 'help' }} />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography variant="body2" className="batch-item-timestamp">
                                        {t('bulkAnalysisPage.batchSelector.lastUpdated', { date: new Date(batch.updated_at).toLocaleString() })}
                                    </Typography>
                                </div>
                            </div>
                        ))
                    ) : (
                        <Typography sx={{ p: 3, textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {t('bulkAnalysisPage.batchSelector.noBatchesFound')}
                        </Typography>
                    )}
                </Box>
            )}

            {batches.length > 0 && !isSelecting && hasMore && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                {isLoading
                ? <CircularProgress size={24} sx={{ color: 'white' }} />
                : (
                    <Button
                    onClick={handleLoadMore}
                    variant="outlined"
                    className="load-more-button"
                    >
                    {t('bulkAnalysisPage.batchSelector.loadMore')}
                    </Button>
                )
                }
            </Box>
            )}

            {/* --- DELETE ALL BUTTON (Conditionally Rendered BELOW List) --- */}
            {batches.length >= 2 && !isSelecting && (
                <Box 
                    sx={{ 
                        mt: 4, 
                        pt: 2, 
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center' 
                    }}
                >
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleOpenDeleteAll}
                        sx={{ 
                            textTransform: 'none',
                            borderColor: 'rgba(211, 47, 47, 0.5)',
                            color: '#ff8a80', 
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                borderColor: '#d32f2f',
                                backgroundColor: '#d32f2f', // Solid red background
                                color: 'white',             // White text for contrast
                                boxShadow: '0 0 10px rgba(211, 47, 47, 0.5)' // Optional: Add a glow
                            }
                        }}
                    >
                        {t('bulkAnalysisPage.deleteAll.button')}
                    </Button>
                </Box>
            )}

            {ReactDOM.createPortal(
                <div className={`confirmation-dialog-overlay ${isCreateDialogOpen ? 'open' : ''}`}  role="dialog" aria-modal="true">
                    {isCreateDialogOpen && (
                        <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()}>
                            <IconButton aria-label="close" onClick={handleCloseCreateDialog} className="dialog-close-button">
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">{t('bulkAnalysisPage.dialogs.create.title')}</h2>
                            <div className="confirmation-dialog-body">
                               {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label={t('bulkAnalysisPage.dialogs.create.label')}
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={newBatchName}
                                    onChange={(e) => setNewBatchName(e.target.value.toLowerCase())}
                                />
                                <TextField
                                    margin="dense"
                                    label={t('bulkAnalysisPage.dialogs.create.descriptionLabel')}
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="standard"
                                    value={newBatchDescription}
                                    onChange={(e) => setNewBatchDescription(e.target.value)}
                                    inputProps={{ maxLength: 250 }}
                                    helperText={`${newBatchDescription.length}/250`}
                                    sx={{
                                        // Target the label text and make it a lighter grey
                                        '& .MuiInputLabel-root': {
                                            color: '#9e9e9e' // A medium grey color for the label
                                        },
                                        // Target the underline of the standard variant
                                        '& .MuiInput-underline:before': {
                                            borderColor: '#e0e0e0' // A very light grey for the underline
                                        },
                                        // Optional: Ensure the hover underline is also subtle
                                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                            borderColor: '#bdbdbd'
                                        }
                                    }}
                                />
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={handleCloseCreateDialog}
                                    variant="outlined"
                                    disabled={isCreating}
                                    sx={{
                                        borderColor: 'var(--secondary-color)',
                                        color: 'var(--secondary-color)',
                                        '&:hover': {
                                            borderColor: 'var(--secondary-color)',
                                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                        }
                                    }}
                                >
                                    {t('bulkAnalysisPage.dialogs.common.cancel')}
                                </Button>
                                <Button
                                    onClick={handleConfirmCreate}
                                    variant="contained"
                                    color="secondary"
                                    autoFocus
                                    disabled={isCreating}
                                    sx={{
                                        backgroundColor: 'var(--secondary-color)',
                                        '&:hover': {
                                            borderColor: 'var(--btn-hover-color)',
                                            backgroundColor: 'var(--btn-hover-color)',
                                        }
                                    }}
                                >
                                    {isCreating ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('bulkAnalysisPage.dialogs.common.create')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}

            {ReactDOM.createPortal(
                <div className={`confirmation-dialog-overlay ${isConfirmOpen ? 'open' : ''}`} onClick={handleCloseConfirm} role="dialog" aria-modal="true">
                    {isConfirmOpen && (
                        <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()}>
                            <IconButton aria-label="close" onClick={handleCloseConfirm} className="dialog-close-button">
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">{t('bulkAnalysisPage.dialogs.delete.title')}</h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">{t('bulkAnalysisPage.dialogs.delete.description', { batchName: batchToDelete?.batch_name })}</p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={handleCloseConfirm}
                                    variant="outlined"
                                    disabled={isDeleting}
                                    sx={{
                                        borderColor: 'var(--secondary-color)',
                                        color: 'var(--secondary-color)',
                                        '&:hover': {
                                            borderColor: 'var(--secondary-color)',
                                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                        }
                                    }}
                                >
                                    {t('bulkAnalysisPage.dialogs.common.cancel')}
                                </Button>
                                <Button
                                    onClick={handleConfirmDelete}
                                    color="error"
                                    variant="contained"
                                    autoFocus
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('bulkAnalysisPage.dialogs.common.delete')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}

            {/* --- DELETE ALL CONFIRMATION DIALOG --- */}
            {ReactDOM.createPortal(
                <div className={`confirmation-dialog-overlay ${isDeleteAllOpen ? 'open' : ''}`} onClick={handleCloseDeleteAll} role="dialog" aria-modal="true">
                    {isDeleteAllOpen && (
                        <div 
                            className="confirmation-dialog-content delete-dialog" // Reusing 'delete-dialog' class from AccountPage if available, or style inline
                            onClick={(e) => e.stopPropagation()}
                            style={{ 
                                backgroundColor: '#1f2937', 
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white !important',
                                position: 'relative'
                            }}
                        >
                            <IconButton 
                                aria-label="close" 
                                onClick={handleCloseDeleteAll} 
                                sx={{ 
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: 'rgba(255, 255, 255, 0.6) !important',
                                    '&:hover': { 
                                        color: 'white !important', 
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title" style={{ color: '#f44336' }}>
                                {t('bulkAnalysisPage.deleteAll.modalTitle')}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '1rem' }}>
                                    {t('bulkAnalysisPage.deleteAll.warningText')}
                                </p>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label={t('bulkAnalysisPage.deleteAll.confirmLabel', { keyword: confirmKeyword })}
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    value={deleteAllConfirmation}
                                    onChange={(e) => setDeleteAllConfirmation(e.target.value)}
                                    // Style text field for dark theme modal
                                    sx={{
                                        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.9)' },
                                        '& .MuiFilledInput-root': { backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white' },
                                        '& .MuiFilledInput-root:hover': { backgroundColor: 'rgba(0, 0, 0, 0.4)' },
                                        '& .MuiFilledInput-root.Mui-focused': { backgroundColor: 'rgba(0, 0, 0, 0.4)' },
                                        '& .MuiFilledInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.3)' },
                                        '& .MuiFilledInput-underline:after': { borderBottomColor: '#f44336' } // Red underline focus
                                    }}
                                />
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button 
                                    onClick={handleCloseDeleteAll} 
                                    variant="text"
                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                >
                                    {t('bulkAnalysisPage.dialogs.common.cancel')}
                                </Button>
                                <Button 
                                    onClick={handleConfirmDeleteAll} 
                                    color="error" 
                                    variant="contained" 
                                    disabled={isDeletingAll || deleteAllConfirmation.toLowerCase() !== confirmKeyword.toLowerCase()}
                                    sx={{ 
                                        backgroundColor: '#d32f2f',
                                        '&:hover': { backgroundColor: '#b71c1c' },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.15)', // Dimmed background
                                            color: 'rgba(255, 255, 255, 0.3)',         // Dimmed text
                                            border: '1px solid rgba(211, 47, 47, 0.3)' 
                                        }
                                    }}
                                >
                                    {isDeletingAll ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('bulkAnalysisPage.deleteAll.confirmButton')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </Box>
    );
};

export default BatchSelector;